import { isAfterDateNow } from '@/helpers/date-helpers'
import { getAggregateSavings } from '@/services/savings'
import { defineStore } from 'pinia'
import { reactive, computed } from 'vue'

export type SavingType =
  | 'BOOKING_WITH_POINTS'
  | 'MEMBER_RATE'
  | 'SUBSCRIPTION'
  | 'DIN'
  | 'SNU'
  | 'BURN_4STAY'

export interface Savings {
  valueInCurrency: number
  type: SavingType
  quantity?: number
}

interface TotalSavings {
  savings: Savings[]
  valueInCurrency: number
}

export interface YearSavings {
  year: number
  savings: Savings[]
  valueInCurrency: number
}

interface GetSavingsInterface {
  requestedCurrencyCode: string
  subscriptionSavings: {
    byCard: {
      number: string
      beginDate: string
      endDate: string
      product: {
        code: string
        label: string
        bin: string
        type: string
        codeTars: string
        active: boolean
      }
      savings: Savings[]
    }[]
    total: TotalSavings
  }
  loyaltySavings: {
    byYear: YearSavings[]
    total: TotalSavings
  }
}
export interface ExperienceItem {
  id: string
  title: string
  url: string
  mainPictureUrl: string
  pricing: {
    startingPrice: number
    currency: string
    rewardPoints: number
  }
  rating: {
    rating: number
    outOf: number
  } | null
}

export interface ActivitiesData {
  booking: {
    hotel: {
      experiences: {
        paginationCursor: string
        items: Array<ExperienceItem>
      }
    }
  }
}

export const useSavingsStore = defineStore('savings', () => {
  const savings: GetSavingsInterface = reactive({
    requestedCurrencyCode: 'EUR',
    subscriptionSavings: {
      byCard: [],
      total: {
        savings: [
          {
            valueInCurrency: 0,
            type: 'SNU'
          }
        ],
        valueInCurrency: 0
      }
    },
    loyaltySavings: {
      byYear: [
        {
          year: 2023,
          savings: [
            {
              valueInCurrency: 0,
              type: 'SNU'
            }
          ],
          valueInCurrency: 0
        }
      ],
      total: {
        savings: [
          {
            valueInCurrency: 0,
            type: 'SNU'
          }
        ],
        valueInCurrency: 0
      }
    }
  })

  const loyaltySavingsTotal = computed(() => {
    return savings.loyaltySavings.total
  })

  const loyaltyReviewsTotal = computed(() => {
    return savings.subscriptionSavings.total
  })

  const loyaltySavingsByYear = computed(() => {
    return savings.loyaltySavings.byYear
  })

  const setSavings = (value: any) => {
    Object.assign(savings, value)
  }

  const getSavingsByYear = (savingYear: number) =>
    savings.loyaltySavings.byYear.find(({ year }) => year === savingYear)

  const getSubscriptionSavingsByCard = (productCode: string, codeTars: string) => {
    const savingsByCard = savings.subscriptionSavings.byCard.filter(
      (card) => card.product.code === productCode && card.product.codeTars === codeTars
    )

    return savingsByCard.length === 1
      ? savingsByCard[0]
      : savingsByCard.find((card) => isAfterDateNow(card.endDate))
  }

  const loadSavings = async () => {
    try {
      const ordersData = await getAggregateSavings()
      setSavings(ordersData)
    } catch (err) {
      console.error(err)
    }
  }

  return {
    savings,
    loadSavings,
    setSavings,
    getSavingsByYear,
    loyaltySavingsTotal,
    loyaltyReviewsTotal,
    loyaltySavingsByYear,
    getSubscriptionSavingsByCard
  }
})
